/** Site Development (Demo Mode only) - index page listing mobilisation/setup projects for
 * opening, onboarding or changing a site. No backend site-development feed exists yet - all
 * figures are static mock data, matching the same "seeded illustrative numbers" approach used
 * by src/pages/EstateManagement. Each row links through to SiteDevelopmentDetail.tsx, the
 * dedicated detail page for that project. The component/file name (StoreDevelopment) is kept
 * as-is to minimise diff noise - only the displayed labels changed to "Site Development". */

import { Box, Chip, Grid, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import { Engineering, AddBusiness, Build, Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';

export const MOCK_PROJECTS = [
  { name: 'Riverside Extension', type: 'Remodel', stage: 'Installation', targetOpen: '2026-09-15', status: 'On Track' },
  { name: 'Northgate New Store', type: 'New Build', stage: 'Procurement', targetOpen: '2026-08-01', status: 'On Track' },
  { name: 'Southgate Relocation', type: 'Relocation', stage: 'Discovery', targetOpen: '2027-01-20', status: 'At Risk' },
  { name: 'Uptown Refresh', type: 'Remodel', stage: 'Design', targetOpen: '2026-11-05', status: 'On Track' },
];

const MOCK_TILES = [
  { key: 'activeProjects', label: 'Active projects', value: `${MOCK_PROJECTS.length}`, subtitle: 'In development', icon: <Engineering />, color: '#1976d2' },
  { key: 'newOpenings', label: 'New openings planned (12 months)', value: '2', subtitle: 'New build locations', icon: <AddBusiness />, color: '#2e7d32' },
  { key: 'remodels', label: 'Remodels in progress', value: '2', subtitle: 'Existing locations', icon: <Build />, color: '#ed6c02' },
  { key: 'atRisk', label: 'Projects at risk', value: '1', subtitle: 'Needs attention', icon: <Warning />, color: '#d32f2f' },
];

export const stageColour = (stage: string) => {
  if (stage === 'Design' || stage === 'Discovery') return 'default';
  if (stage === 'Testing' || stage === 'Ready for Launch' || stage === 'Live') return 'success';
  return 'primary';
};

const SiteDevelopment = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const theme = useTheme();
  const [projectsPage, setProjectsPage] = useState(0);

  return (
    <PageArea>
      <PageTitle
        subTitle={t('storeDevelopmentSubtitle', 'Track new builds, remodels and relocations as locations move from discovery through to go-live.')}>
        {t('storeDevelopmentTitle', 'Location Development')}
      </PageTitle>

      <Grid container
        spacing={4}
        sx={{ mb: 6, mt: 2 }}>
        {MOCK_TILES.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={3}
            key={tile.key}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {t(`storeDevelopment${tile.key.charAt(0).toUpperCase()}${tile.key.slice(1)}`, tile.label)}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Section>
          <TableHeader>
            {t('storeDevelopmentProjectsTitle', 'Development projects')}
            <Pill text={`${MOCK_PROJECTS.length} ${t('storeDevelopmentProjectsLabel', 'projects')}`} />
          </TableHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('storeDevelopmentProject', 'Project')}</TableCell>
                  <TableCell>{t('storeDevelopmentType', 'Type')}</TableCell>
                  <TableCell>{t('storeDevelopmentStage', 'Stage')}</TableCell>
                  <TableCell>{t('storeDevelopmentTargetOpen', 'Target Open')}</TableCell>
                  <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOCK_PROJECTS.map((row) => (
                  <TableRow key={row.name}
                    hover
                    onClick={() => navigate(`/estate-management/site-development/${encodeURIComponent(row.name)}`)}
                    sx={{ cursor: 'pointer' }}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>
                      <Chip label={row.stage}
                        size="small"
                        color={stageColour(row.stage)}
                        variant="outlined" />
                    </TableCell>
                    <TableCell>{row.targetOpen}</TableCell>
                    <TableCell>
                      <Chip label={row.status}
                        size="small"
                        color={row.status === 'At Risk' ? 'warning' : 'success'}
                        variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination page={projectsPage}
            setPage={setProjectsPage}
            maxPages={1}
            useNoDataMessage={false}
            breakpointForChangeDisplay={120000} />
        </Section>
      </Box>
    </PageArea>
  );
};

export default SiteDevelopment;
